import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { Download, Loader2, CheckCircle2, TriangleAlert } from 'lucide-react';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import { FileDropzone } from '../../../../../components/ui/FileDropzone';
import { uploadOrdersExcel } from '../../../../../features/deliveries/deliveriesThunks';
import {
  fetchSellerOptions,
  fetchSellerWarehouseOptions,
} from '../../../../../features/sellers/sellersThunks';
import { clearSellerWarehouseOptions } from '../../../../../features/sellers/sellersSlice';
import { validateOrdersSheet } from '../../../../../features/deliveries/orderUploadUtils';
import {
  EXCEL_TEMPLATES,
  ACCEPTED_SHEET_EXTENSIONS,
} from '../../../../../features/deliveries/orderUploadConstants';
import {
  orderUploadCleared,
  selectOrderUpload,
} from '../../../../../features/deliveries/orderUploadSlice';
import { useOrderUploadWatchdog } from '../../../../../features/deliveries/useOrderUploadWatchdog';
import { jobFocused, jobUnfocused } from '../../../../../features/feedback/feedbackSlice';
import { NOTIFICATION_KIND } from '../../../../../features/notifications/notificationsConstants';
import { OrderUploadProgress } from '../../../../../components/upload/OrderUploadProgress';

/**
 * Bulk order creation from a spreadsheet.
 *
 * The file is parsed and validated in the browser before it is sent, so the
 * operator learns that the template is wrong without waiting for a round trip.
 * The orders themselves are created by a worker: the upload answers as soon as
 * the file is stored, and from there the screen follows the socket.
 *
 * @param {object} props
 * @param {string} props.country - ISO2 code of the signed-in admin
 */
export const MassiveUploadPanel = ({ country }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isSubmittingUpload } = useSelector((state) => state.deliveries);
  const upload = useSelector(selectOrderUpload);
  const {
    sellerOptions,
    loadingSellerOptions,
    sellerWarehouseOptions,
    loadingSellerWarehouseOptions,
  } = useSelector((state) => state.sellers);

  const [sellerId, setSellerId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // A discarded task and a long file look the same from here: both are a bar
  // that does not move. This is what tells them apart.
  useOrderUploadWatchdog();

  // This screen shows the upload, its progress and its failure; toasts only
  // speak once the user has left it.
  useEffect(() => {
    dispatch(jobFocused(NOTIFICATION_KIND.ORDER_UPLOAD));
    return () => dispatch(jobUnfocused(NOTIFICATION_KIND.ORDER_UPLOAD));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchSellerOptions());
  }, [dispatch]);

  const templateUrl = EXCEL_TEMPLATES[country] || EXCEL_TEMPLATES.CL;
  const isReadyToUpload = Boolean(sellerId && warehouseId && file && !fileError);

  const handleSellerChange = (value) => {
    setSellerId(value);
    // A warehouse from the previous seller must never reach the payload.
    setWarehouseId('');
    dispatch(clearSellerWarehouseOptions());
    dispatch(fetchSellerWarehouseOptions(value));
  };

  /**
   * Parses the sheet and validates it against the country's template.
   *
   * @param {File} selected
   */
  const handleFileSelected = (selected) => {
    setFeedback(null);
    setFileError(null);
    setIsParsing(true);

    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const workbook = XLSX.read(event.target.result, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });

        const { isValid, message } = validateOrdersSheet(rows, country);

        if (!isValid) {
          setFileError(message);
          setFile(null);
        } else {
          setFile(selected);
          setFeedback({
            type: 'info',
            message: `${rows.length} ${rows.length === 1 ? 'orden lista' : 'órdenes listas'} para cargar.`,
          });
        }
      } catch (error) {
        console.error('Error parsing orders spreadsheet:', error);
        setFileError('No se pudo leer el archivo. Verifica que sea una planilla válida.');
        setFile(null);
      } finally {
        setIsParsing(false);
      }
    };

    reader.onerror = () => {
      setFileError('No se pudo leer el archivo.');
      setFile(null);
      setIsParsing(false);
    };

    reader.readAsArrayBuffer(selected);
  };

  const handleUpload = () => {
    dispatch(uploadOrdersExcel({ file, sellerId, warehouseId, country })).unwrap().catch((error) => {
      setFeedback({
        type: 'error',
        message: typeof error === 'string' ? error : 'Error al cargar el archivo.',
      });
    });
  };

  const handleReset = () => {
    dispatch(orderUploadCleared());
    dispatch(clearSellerWarehouseOptions());
    setSellerId('');
    setWarehouseId('');
    setFile(null);
    setFileError(null);
    setFeedback(null);
  };

  // Once the file is handed over there is nothing left to fill in, and leaving
  // the form on screen invites a second upload of the same orders.
  if (upload.phase !== 'idle') {
    return (
      <OrderUploadProgress
        {...upload}
        onReset={handleReset}
        onGoToDeliveries={() => {
          dispatch(orderUploadCleared());
          navigate('/deliveries');
        }}
      />
    );
  }

  return (
    <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 sm:p-8 shadow-sm border border-border space-y-6 animate-in fade-in duration-300">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-ninja-dark dark:text-white">
            Carga tu orden mediante Carga Masiva
          </h3>
          <p className="text-sm text-text-secondary mt-1">
            Selecciona el seller y su bodega de origen, luego adjunta la planilla.
          </p>
        </div>
        <a
          href={templateUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-2 rounded-xl border border-border text-text-secondary hover:text-ninja-dark dark:hover:text-white hover:border-ninja-dark dark:hover:border-white font-semibold text-sm transition-colors whitespace-nowrap"
        >
          <Download size={16} />
          Descargar plantilla
        </a>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <FilterableSelect
          variant="form"
          label="Seller"
          value={sellerId}
          onChange={handleSellerChange}
          options={sellerOptions}
          disabled={loadingSellerOptions}
          placeholder={loadingSellerOptions ? 'Cargando sellers...' : 'Seleccionar...'}
          searchPlaceholder="Buscar seller..."
        />

        <FilterableSelect
          variant="form"
          label="Bodega de origen"
          value={warehouseId}
          onChange={setWarehouseId}
          options={sellerWarehouseOptions}
          disabled={!sellerId || loadingSellerWarehouseOptions}
          placeholder={
            !sellerId
              ? 'Primero selecciona un seller'
              : loadingSellerWarehouseOptions
                ? 'Cargando bodegas...'
                : 'Seleccionar...'
          }
          searchPlaceholder="Buscar bodega..."
        />
      </div>

      {/* The dropzone only appears once the orders have a destination. */}
      {sellerId && warehouseId && (
        <div>
          {isParsing ? (
            <div className="flex items-center justify-center gap-3 py-10 border border-dashed border-border rounded-2xl bg-ninja-gray/40 dark:bg-[#111]">
              <Loader2 size={18} className="text-ninja-green animate-spin" />
              <span className="text-sm text-text-secondary">Revisando el archivo...</span>
            </div>
          ) : (
            <FileDropzone
              file={file}
              onFileSelected={handleFileSelected}
              onClear={() => {
                setFile(null);
                setFileError(null);
                setFeedback(null);
              }}
              extensions={ACCEPTED_SHEET_EXTENSIONS}
              error={fileError}
              hint="Formatos aceptados: .xlsx, .xls y .csv"
            />
          )}
        </div>
      )}

      {feedback && (
        <div
          className={
            feedback.type === 'error'
              ? 'flex items-start gap-2 bg-red-50 dark:bg-red-900/20 text-danger p-3 rounded-xl border border-red-200 dark:border-red-900 text-sm'
              : 'flex items-start gap-2 bg-ninja-greenLight dark:bg-ninja-green/10 text-ninja-green p-3 rounded-xl border border-ninja-green/20 text-sm'
          }
        >
          {feedback.type === 'error' ? (
            <TriangleAlert size={16} className="mt-0.5 shrink-0" />
          ) : (
            <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
          )}
          {feedback.message}
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleUpload}
          disabled={!isReadyToUpload || isSubmittingUpload}
          className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-ninja-green hover:bg-ninja-green/90 text-white font-bold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmittingUpload && <Loader2 size={16} className="animate-spin" />}
          {isSubmittingUpload ? 'Cargando...' : 'Cargar archivo'}
        </button>
      </div>
    </div>
  );
};
